import type { InvestmentCase, InvestmentCaseFounder } from '../types';

export const INVESTMENT_CASE_RATING_DIMENSIONS = [
  { key: 'founderAmbition', label: 'Founder ambition' },
  { key: 'timeToRevenue', label: 'Time to revenue' },
  { key: 'exitExperience', label: 'Exit experience' },
  { key: 'founderSkillset', label: 'Founder skillset' },
  { key: 'marketSize', label: 'Market size' },
] as const;

type RatingDimensionKey = (typeof INVESTMENT_CASE_RATING_DIMENSIONS)[number]['key'];

const DIMENSIONS: RatingDimensionKey[] = INVESTMENT_CASE_RATING_DIMENSIONS.map((d) => d.key);

/** Mean of the five 1–6 ratings. */
export function investmentCaseAverageScore(c: InvestmentCase): number {
  let sum = 0;
  for (const k of DIMENSIONS) {
    sum += clampRating(c[k]);
  }
  return sum / DIMENSIONS.length;
}

export type InvestmentVerdict = 'INVEST' | 'DONT';

/** Average ≥ 4 → INVEST (midpoint on a 1–6 scale). */
export function investmentCaseVerdict(c: InvestmentCase): InvestmentVerdict {
  return investmentCaseAverageScore(c) >= 4 ? 'INVEST' : 'DONT';
}

export function clampRating(n: unknown): number {
  const x = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(x)) return 3;
  return Math.min(6, Math.max(1, Math.round(x)));
}

/** Omit empty founder fields — Firestore rejects `undefined` inside array objects. */
export function founderForStorage(f: InvestmentCaseFounder): InvestmentCaseFounder | null {
  const name = f.name?.trim();
  const linkedinUrl = f.linkedinUrl?.trim();
  const profileText = f.profileText?.trim();
  if (!name && !linkedinUrl && !profileText) return null;
  const out: InvestmentCaseFounder = { id: f.id };
  if (name) out.name = name;
  if (linkedinUrl) out.linkedinUrl = linkedinUrl;
  if (profileText) out.profileText = profileText;
  return out;
}

export function foundersForStorage(
  founders: InvestmentCaseFounder[] | undefined
): InvestmentCaseFounder[] | undefined {
  if (!founders?.length) return undefined;
  const kept = founders
    .map(founderForStorage)
    .filter((f): f is InvestmentCaseFounder => f !== null);
  return kept.length > 0 ? kept : undefined;
}
